/**
 * The invitation to say how it went.
 *
 * One component, three places to stand: a card on a light page, a tile inside
 * the navy brand column of a sign-in screen, and a band across the foot of the
 * portal. The same words everywhere, because the thing a customer has to learn
 * only once is that the centre asks, that it takes two minutes, and that no
 * account is needed. A sentence of grey text under a form does not teach that;
 * this is meant to be the one thing on the page that is not a form.
 */

import { ArrowRight, MessageSquareHeart, Star } from "lucide-react";
import { Link } from "react-router-dom";

import { cn, toTitleCase } from "@/lib/format";

export const FEEDBACK_PATH = "/feedback/new";

const HEADLINE = "How did we do?";
const PITCH =
  "Two minutes, no sign-in, anonymous if you like. Your words go straight to the Director and Quality Assurance.";
const CTA = "Tell us how it went";

function Stars({ className }: { className?: string }) {
  return (
    <span className={cn("flex items-center gap-0.5", className)} aria-hidden="true">
      {[0, 1, 2, 3, 4].map((index) => (
        <Star
          key={index}
          size={13}
          fill="currentColor"
          className="transition-transform duration-200 group-hover:-translate-y-0.5"
          style={{ transitionDelay: `${index * 40}ms` }}
        />
      ))}
    </span>
  );
}

export function FeedbackInvite({
  variant = "card",
  className,
}: {
  variant?: "card" | "dark" | "band";
  className?: string;
}) {
  if (variant === "band") {
    return (
      <div className={cn("bg-sidebar text-sidebar-foreground", className)}>
        <Link
          to={FEEDBACK_PATH}
          className="group mx-auto flex max-w-6xl flex-wrap items-center gap-x-5 gap-y-3 px-5 py-5"
        >
          <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-gold text-brand shadow-lg shadow-gold/30">
            <MessageSquareHeart size={22} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-base font-semibold text-white">{toTitleCase(HEADLINE)}</span>
              <Stars className="text-gold" />
            </span>
            <span className="mt-0.5 block text-xs text-sidebar-muted">{PITCH}</span>
          </span>
          <span className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-brand transition group-hover:bg-white">
            {toTitleCase(CTA)}
            <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </span>
        </Link>
      </div>
    );
  }

  if (variant === "dark") {
    return (
      <Link
        to={FEEDBACK_PATH}
        className={cn(
          "group block rounded-2xl border border-white/15 bg-white/5 p-4 transition hover:border-gold/60 hover:bg-white/10",
          className,
        )}
      >
        <span className="flex items-start gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-gold text-brand">
            <MessageSquareHeart size={19} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-2">
              <span className="text-sm font-semibold text-white">{toTitleCase(HEADLINE)}</span>
              <Stars className="text-gold" />
            </span>
            <span className="mt-1 block text-xs leading-relaxed text-sidebar-muted">{PITCH}</span>
            <span className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-gold">
              {toTitleCase(CTA)}
              <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
            </span>
          </span>
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={FEEDBACK_PATH}
      className={cn(
        "group relative block overflow-hidden rounded-2xl border border-gold/50 bg-gradient-to-br from-gold-tint via-card to-brand-tint p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md",
        className,
      )}
    >
      {/* A large, faint mark in the corner: the card should read as an
          invitation from across the room, not as another panel of settings. */}
      <MessageSquareHeart
        size={120}
        aria-hidden="true"
        className="pointer-events-none absolute -right-6 -top-6 text-gold/15 transition-transform duration-300 group-hover:rotate-6"
      />
      <span className="relative flex items-start gap-4">
        <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-gold text-brand shadow-md shadow-gold/40">
          <MessageSquareHeart size={24} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-lg font-semibold tracking-tight text-foreground">
              {toTitleCase(HEADLINE)}
            </span>
            <Stars className="text-gold" />
          </span>
          <span className="mt-1 block text-sm text-muted-foreground">{PITCH}</span>
          <span className="btn btn-primary mt-4 inline-flex">
            {toTitleCase(CTA)}
            <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </span>
        </span>
      </span>
    </Link>
  );
}
